import { defineStore } from "pinia";
import axiosIns from "../composables/axios";
import { router } from "@/plugins/1.router";

import websocket from "@/websocket";

import { useNotificationStore } from "@/store/notification";
import { notifyBug } from "../utils/notifyBug";
import { addUserMetadata } from "@/plugins/bugsnag";
import { AmplitudeService } from "../services/amplitude";

export interface IUser {
  userId: number;
  name: string;
  email: string;
  phone: string;
  role: string;
  permissions: any[];
  owner: boolean;
  sysAdmin?: boolean;
  sysAccessLevel?: number | string;
  validated: boolean;
  companyId: number;
  token: string;
  firstAccess: boolean;
}

export interface NewUserRegisterDTO {
  userName: string;
  email: string;
  password: string;
  whatsapp: string;
}

export interface UserLoginDTO {
  email: string;
  password: string;
}

export interface ActiveAccountDTO {
  email: string;
  token: string;
}

export interface ResetPasswordDTO {
  token: string;
  email: string;
  password: string;
}

export enum PermissionsAction {
  READ = "read",
  WRITE = "write",
  CREATE = "create",
  DELETE = "delete",
}

export enum PermissionModules {
  DASHBOARD = "dashboard",
  CALENDAR = "calendar",
  COMPANY = "company",
  CHAIRS = "chairs",
  ANAMNESE = "anamneses",
  PROFESSIONALS = "professionals",
  SUPPLIERS = "suppliers",
  PATIENTS = "patients",
  COST_REFERENCE = "cost_reference",
  BUDGETS = "budgets",
  TREATMENTS = "treatments",
  ACCOUNTS = "accounts",
  ANANMENSE_TEMPLATE = "anamnese_template",
  PATIENT_INVOICES = "patient-invoices",
  FINANCY = "financy",
  EXPENSES = "expenses",
  FINANCE_CATEGORY = "finance-category",
  INVOICES = "invoices",
  CASHFLOW = "cashflow",
  REPORTS = "reports",
  DRE = "dre",
  REPORTS_BUDGETS = "reports-budgets",
  REPORTS_TREATMENTS = "reports-treatments",
  REPORTS_CALENDAR = "reports-calendar",
  FORECAST_ACTUAL = "forecast_actual",
  PROGRESS_NOTES = "progress-notes",
  PRESCRIPTION = "prescription",
  INTERNAL_PRESCRIPTION = "internal-prescription",
  NOTIFICATION = "notification",
  NOTIFICATION_SETTINGS = "notification-settings",
  OFFICIAL_WHATSAPP_ENABLED = "officialWhatsappEnabled",
}

export const userStore = defineStore("user", {
  state: (): {
    user: IUser | null;
    loading: boolean;
    plan: string;
    permissionsLoaded: boolean;
  } => {
    const userData = localStorage.getItem("user");
    const user = userData ? JSON.parse(userData) : null;
    // Só considerar permissões carregadas se o usuário existir E tiver permissões válidas
    const hasValidPermissions =
      user && Array.isArray(user.permissions) && user.permissions.length > 0;

    return {
      user: user,
      loading: false,
      plan: "free",
      permissionsLoaded: hasValidPermissions,
    };
  },
  actions: {
    async initializeApp() {
      const userData = localStorage.getItem("user");
      if (userData) {
        try {
          const user = JSON.parse(userData);
          if (user.token) {
            // Verifica se o token é válido e atualiza as permissões
            await this.updateUserInfo();
            // Só marcar como carregado se o usuário tiver permissões válidas
            if (
              this.user &&
              Array.isArray(this.user.permissions) &&
              this.user.permissions.length > 0
            ) {
              this.permissionsLoaded = true;
            } else {
              console.warn("Usuário carregado mas sem permissões válidas");
              this.permissionsLoaded = false;
            }
          } else {
            this.permissionsLoaded = false;
          }
        } catch (error) {
          console.error("Erro ao inicializar app:", error);
          this.permissionsLoaded = false;
          // Só fazer logout se for erro de autenticação
          if (error && typeof error === "object" && "response" in error) {
            const axiosError = error as any;
            if (
              axiosError.response?.status === 401 ||
              axiosError.response?.status === 403
            ) {
              this.logout();
            }
          }
        }
      } else {
        this.permissionsLoaded = false;
      }
    },
    async login(UserLoginDTO: UserLoginDTO): Promise<void> {
      const isNotValidated = await this.checkAccountValidationByEmail(
        UserLoginDTO.email
      );

      if (isNotValidated) {
        return;
      }
      this.loading = true;
      try {
        const res = await axiosIns.post("login", UserLoginDTO);
        this.$patch({
          user: res?.data,
          permissionsLoaded:
            Array.isArray(res?.data?.permissions) &&
            res.data.permissions.length > 0,
        });
        localStorage.setItem("user", JSON.stringify(res?.data));

        // Após login, buscar os dados completos do usuário (inclui permissões/sysAccessLevel atuais)
        await this.updateUserInfo();
        addUserMetadata(this.user);

        if (!this.permissionsLoaded) {
          console.warn("Login realizado mas sem permissões válidas");
        }

        router.push("/dashboard");
        await this.checkFirstAccess();
      } catch (error) {
        console.error("Erro no login:", error);
        this.permissionsLoaded = false;
      } finally {
        this.loading = false;
      }
    },
    async checkFirstAccess(): Promise<void> {
      try {
        const userData = localStorage.getItem("user");
        if (!userData) {
          return;
        }

        const parsedUser = JSON.parse(userData);
        if (!parsedUser || !parsedUser.token) {
          return;
        }

        const { token } = parsedUser;
        if (token && this.user?.firstAccess) {
          try {
            const res = await axiosIns.get(`user/first-access`);
            if (this.user && res?.data) {
              this.user.firstAccess = res.data.firstAccess;
              localStorage.setItem("user", JSON.stringify(this.user));
              addUserMetadata(this.user);
            }
          } catch (error) {
            console.error("Error getting first access:", error);
          }
        }
      } catch (error) {
        console.error("Error in checkFirstAccess:", error);
      }
    },
    logout(): void {
      this.user = null;
      this.permissionsLoaded = false;
      localStorage.removeItem("user");
      websocket.disconnect();
      router.push("/auth/login");
    },
    async updateUserInfo(): Promise<void> {
      const user = JSON.parse(localStorage.getItem("user") as string);
      const id = user?.userId ?? user?.id;

      if (!id) {
        throw new Error("ID do usuário não encontrado");
      }

      const res = await axiosIns.get(`user/${id}`);
      const { token } = JSON.parse(localStorage.getItem("user") as string);
      const logged = { ...res.data, token };
      
      // Usar $patch para garantir que Pinia detecte as mudanças e dispare reatividade
      this.$patch({
        user: logged,
        permissionsLoaded: Array.isArray(logged.permissions) && logged.permissions.length > 0,
      });

      localStorage.setItem("user", JSON.stringify(logged));

    },
    async refreshToken(): Promise<void> {
      try {
        const res = await axiosIns.post("refresh-token");
        if (res?.data) {
          // Verificar se as permissões são válidas antes de marcar como carregadas
          const hasValidPermissions =
            Array.isArray(res.data.permissions) &&
            res.data.permissions.length > 0;

          // Usar $patch para garantir que o Pinia detecte a mudança
          this.$patch({
            user: res.data,
            permissionsLoaded: hasValidPermissions,
          });
          localStorage.setItem("user", JSON.stringify(res.data));
          addUserMetadata(res.data);
        }
      } catch (error) {
        console.error("Erro ao atualizar token:", error);
        // Se falhar, fazer logout para forçar novo login
        this.logout();
      }
    },
    async register(NewUserRegisterDTO: NewUserRegisterDTO): Promise<void> {
      localStorage.setItem(
        "user",
        JSON.stringify({
          email: NewUserRegisterDTO.email,
        })
      );
      const isNotValidated = await this.checkAccountValidationByEmail(
        NewUserRegisterDTO.email
      );

      if (isNotValidated) {
        return;
      }

      try {
        const res = await axiosIns.post("register", NewUserRegisterDTO);
        localStorage.setItem("user", JSON.stringify(res?.data));
        localStorage.setItem("register-password", NewUserRegisterDTO.password);
        addUserMetadata(res.data);
        router.push("/auth/confirm-code");
        const notificationStore = useNotificationStore();
        notificationStore.notifySuccess({
          title: "Sucesso",
          text: "Conta criada com sucesso",
        });
        AmplitudeService.trackEvent("Register Success", res.data);
      } catch (error) {
        console.error(error);
        AmplitudeService.trackEvent("Register Error", {
          error,
        });
      }
    },
    async activateAccount(ActiveAccountDTO: ActiveAccountDTO): Promise<void> {
      this.loading = true;
      await axiosIns
        .put(`validation_account/activate/`, ActiveAccountDTO)
        .then(() => {
          this.user = { ...this.user, validated: true };
          localStorage.setItem("user", JSON.stringify(this.user));
          const notificationStore = useNotificationStore();
          notificationStore.notifySuccess({
            title: "Conta ativada",
            text: "Validamos seu telefone com sucesso",
          });
          this.loading = false;

          const password = localStorage.getItem("register-password");
          if (password) {
            localStorage.removeItem("register-password");
            this.login({ email: ActiveAccountDTO.email, password });
          } else {
            router.push("/auth/login");
          }
        })
        .catch((error) => {
          const notificationStore = useNotificationStore();
          notificationStore.notifyFail({
            title: "Erro",
            text: "Não foi possível ativar a conta",
          });
          this.loading = false;
        });
    },
    async checkAccountValidationByEmail(email: string): Promise<boolean> {
      const status = await axiosIns
        .get(`validation_account/status/email/${email}`)
        .then((res) => res.data.status);

      const isNotValidated = status === "NOT_VALIDATED";
      if (isNotValidated) {
        router.push("/auth/confirm-code");
      }
      return isNotValidated;
    },
    async resendToken(email: string): Promise<void> {
      this.loading = true;
      await axiosIns
        .post(`validation_account/send_token_again`, { email })
        .then(() => {
          const notificationStore = useNotificationStore();
          notificationStore.notifySuccess({
            title: "Código enviado",
            text: "Confirme o código recebido em seu Whatsapp",
          });
          this.loading = false;
        })
        .catch((error) => {
          const notificationStore = useNotificationStore();
          notificationStore.notifyFail({
            title: "Erro",
            text: "Não foi possível ativar a conta",
          });
          this.loading = false;
        });
    },
    async resetPassword(ResetPasswordDTO: ResetPasswordDTO): Promise<void> {
      await axiosIns.post("reset_password", ResetPasswordDTO).then(() => {
        const notificationStore = useNotificationStore();
        notificationStore.notifySuccess({
          title: "Sucesso",
          text: "Siga as instruções enviadas para o seu email.",
        });
        router.push("/auth/login");
      });
    },
    async forgotPassword(email: string): Promise<void> {
      await axiosIns.post("forgot_password", { email }).then(() => {
        const notificationStore = useNotificationStore();
        notificationStore.notifySuccess({
          title: "Sucesso",
          text: "Siga as instruções enviadas para o seu email.",
        });
      });
    },
    async uploadUserImg(croppedImageBlob: Blob, UserId: number | null) {
      const formData = new FormData();
      formData.append("file", croppedImageBlob, "user-profile.jpeg");

      const url = UserId
        ? `user/profile-picture/${UserId}`
        : "user/profile-picture";

      return axiosIns
        .put(url, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => {
          const notificationStore = useNotificationStore();
          notificationStore.notifySuccess({
            title: "Sucesso",
            text: "Imagem salva com sucesso!",
          });

          return response.data;
        })
        .catch((error) => {
          console.error("Erro ao fazer upload da imagem:", error);
          const notificationStore = useNotificationStore();
          notificationStore.notifyFail({
            title: "Erro",
            text: "Ocorreu um erro ao salvar a imagem.",
          });

          throw error;
        });
    },
    async changePassword(payload: object) {
      return axiosIns
        .put("change_password", payload)
        .then((response) => {
          const notificationStore = useNotificationStore();
          notificationStore.notifySuccess({
            title: "Sucesso",
            text: "Senha alterada com sucesso!",
          });

          return response.data;
        })
        .catch((error) => {
          console.error("Erro ao fazer alterar senha:", error);
          const notificationStore = useNotificationStore();
          notificationStore.notifyFail({
            title: "Erro",
            text: "Ocorreu um erro ao alterar a senha.",
          });

          throw error;
        });
    },
  },
  getters: {
    userGetter: (state: any) => state.user,
    isValidated: (state: any) => state.user?.validated || false,
    storagedUser: (state: any) =>
      JSON.parse(localStorage.getItem("user") as string),
    userPermission: (state: any) => {
      // Garantir que sempre retorna um array para manter reatividade
      return Array.isArray(state.user?.permissions)
        ? state.user.permissions
        : [];
    },
    isOwner: (state: any) => state.user?.owner || false,
    isSysAdmin: (state: any) => {
      // sysAdmin é um campo específico no banco, não baseado em role
      return state.user?.sysAdmin === true;
    },
    hasPermission: (state: any) => (module: string, action: string) => {
      // Regra 1: SysAdmin tem acesso total a tudo
      if (state.user?.sysAdmin) return true;

      // Verificar se módulo está nas permissões (que já foram filtradas pelo plano)
      if (!state.user?.permissions || state.user.permissions.length === 0) {
        return false;
      }

      // Mapeamento de módulos para resolver discrepâncias de nomes
      const moduleMapping: Record<string, string[]> = {
        anamnese: ['anamnese', 'anamneses'],
        anamneses: ['anamnese', 'anamneses'],
        financy: ['financy', 'finance'],
        finance: ['financy', 'finance'],
      };

      // Verificar se módulo está no plano (considerando mapeamento)
      let moduleInPlan = state.user.permissions.some(
        (p: any) => p.module === module
      );

      // Se não encontrou diretamente, tentar usando mapeamento
      if (!moduleInPlan && moduleMapping[module]) {
        moduleInPlan = state.user.permissions.some((p: any) =>
          moduleMapping[module].includes(p.module)
        );
      }

      // Regra 2: Se módulo não está disponível no plano, negar acesso
      if (!moduleInPlan) {
        return false;
      }

      // Regra 3: Se houver acesso ao plano no módulo e for owner, pode ter acesso
      if (state.user?.owner) {
        return true;
      }

      // Regra 4: Se houver acesso ao plano, não for owner, tem que verificar as permissões individuais
      // Buscar permissão considerando mapeamento
      let permission = state.user.permissions.find(
        (p: any) => p.module === module
      );

      // Se não encontrou, tentar usando mapeamento
      if (!permission && moduleMapping[module]) {
        for (const mappedModule of moduleMapping[module]) {
          permission = state.user.permissions.find(
            (p: any) => p.module === mappedModule
          );
          if (permission) break;
        }
      }

      if (!permission) {
        return false;
      }

      return permission[action] === true;
    },
  },
});
